<template>
  <div class="formula-editor">
    <codemirror
      ref="codeEditor"
      v-model="formulaStr"
      :options="codeMirrorOptions"
      @input="handleCodeMirrorChange"
    ></codemirror>
  </div>
</template>

<script>
import {codemirror} from 'vue-codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/idea.css'
import 'codemirror/mode/javascript/javascript.js'

export default {
  name: 'FormulaEditor',
  components: {codemirror},

  props: {
    value: {
      type: String,
      default: ''
    },
    availableVariables: {
      type: Array,
      default: () => []
    },
    allFunctionNames: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      // 公式字符串
      formulaStr: this.value,

      // CodeMirror配置选项
      codeMirrorOptions: {
        mode: 'text/javascript',
        theme: 'idea',
        line: true,
        lineNumbers: false,
        lineWrapping: true,
        tabSize: 4,
        placeholder: '请输入公式'
      }
    }
  },

  computed: {
    // 获取CodeMirror编辑器实例
    codeMirrorEditor() {
      return this.$refs.codeEditor?.codemirror
    }
  },

  mounted() {
    // 组件挂载后聚焦编辑器并高亮公式
    this.focusEditor()
    this.highlightFormula()
  },

  methods: {
    handleChangeTemplate(newVal) {
      if (this.codeMirrorEditor && newVal !== this.codeMirrorEditor.getValue()) {
        this.formulaStr = newVal || '';
        this.codeMirrorEditor.setValue(this.formulaStr);
        this.$nextTick(() => {
          this.highlightFormula();
        });
      }
    },

    // CodeMirror输入变化处理
    handleCodeMirrorChange() {
      this.$emit('input', this.formulaStr)
      this.$nextTick(() => this.highlightFormula())
    },

    // 插入内容到编辑器
    insertContent(content) {
      if (!this.codeMirrorEditor) return

      // 去除变量名中的多余空格，特别是表名.字段名格式中的空格
      const cleanContent = content.replace(/\.\s+/g, '_')

      // 保存当前光标位置
      const cursor = this.codeMirrorEditor.getCursor()
      
      this.codeMirrorEditor.replaceSelection(cleanContent)
      this.updateFormulaStr()
      
      // 恢复光标位置到插入内容的末尾
      this.codeMirrorEditor.setCursor({line: cursor.line, ch: cursor.ch + cleanContent.length})
      this.codeMirrorEditor.focus()
    },

    // 插入函数到编辑器
    insertFunction(functionName) {
      if (!this.codeMirrorEditor) return

      this.codeMirrorEditor.replaceSelection(`${functionName}()`)
      const cursor = this.codeMirrorEditor.getCursor()
      this.codeMirrorEditor.setCursor({line: cursor.line, ch: cursor.ch - 1})
      this.afterInsertOperation()
    },

    // 插入操作后的公共处理逻辑
    afterInsertOperation() {
      this.updateFormulaStr()
      this.focusEditor()
    },

    // 更新公式字符串并触发输入事件
    updateFormulaStr() {
      if (!this.codeMirrorEditor) return

      this.formulaStr = this.codeMirrorEditor.getValue()
      this.$emit('input', this.formulaStr)
      this.$nextTick(() => this.highlightFormula())
    },

    // 高亮公式中的变量和函数
    highlightFormula() {
      if (!this.codeMirrorEditor) return

      // 清除所有旧的标记
      const marks = this.codeMirrorEditor.getAllMarks()
      marks.forEach(mark => mark.clear())

      if (!this.formulaStr) return

      // 高亮所有行
      const lineCount = this.codeMirrorEditor.lineCount()
      for (let line = 0; line < lineCount; line++) {
        const lineContent = this.codeMirrorEditor.getLine(line)
        this.highlightVariablesInLine(lineContent, line)
        this.highlightFunctionsInLine(lineContent, line)
      }
    },

    // 高亮行中的变量
    highlightVariablesInLine(lineContent, line) {
      // 如果行内容只包含数值和运算符，则不进行变量高亮检测
      const onlyNumbersAndOperators = /^[\d+\-*/.%^=<>!&|()[\]{} \s\t]+$/;
      if (onlyNumbersAndOperators.test(lineContent.trim())) {
        return;
      }

      // 处理嵌套的变量结构
      const variableNames = []

      // 遍历所有变量分类
      this.availableVariables.forEach(category => {
        // 确保分类有functions属性且是数组
        if (category.children && Array.isArray(category.children)) {
          // 提取所有变量名
          category.children.forEach(item => {
            if (item && item.label && (item.dataType === 2 || item.dataType === 4)) {
              // 同时添加原始变量名和清理后的变量名（去除空格）
              const rawVariable = category.label + '_' + item.label
              const cleanVariable = rawVariable.replace(/\.\s+/g, '_')

              // 只添加唯一的变量名
              if (!variableNames.includes(rawVariable)) {
                variableNames.push(rawVariable)
              }
              if (cleanVariable !== rawVariable && !variableNames.includes(cleanVariable)) {
                variableNames.push(cleanVariable)
              }
            }
          })
        }
      })

      // 按长度排序变量，确保长变量优先匹配
      const sortedVariables = [...variableNames].sort((a, b) => b.length - a.length)

      // 转换为小写用于不区分大小写的匹配
      const lowerLineContent = lineContent.toLowerCase()

      // 标记所有有效变量
      sortedVariables.forEach(variable => {
        const lowerVariable = variable.toLowerCase()
        let position = 0

        // 使用不区分大小写的方式查找变量名
        while ((position = lowerLineContent.indexOf(lowerVariable, position)) !== -1) {
          // 确保原始内容中对应位置的字符是相同的（不区分大小写）
          if (lineContent.substring(position, position + variable.length).toLowerCase() === lowerVariable) {
            // 检查是否是完整变量（避免部分匹配）
            const isStartOfWord = position === 0 || !/[a-zA-Z0-9_\u4e00-\u9fa5]/.test(lineContent.charAt(position - 1))
            const isEndOfWord = position + variable.length >= lineContent.length ||
              !/[a-zA-Z0-9_\u4e00-\u9fa5.]/.test(lineContent.charAt(position + variable.length))

            if (isStartOfWord && isEndOfWord) {
              this.markTextRange(
                {line, ch: position},
                {line, ch: position + variable.length},
                'cm-field'
              )
            }
          }
          position += variable.length
        }
      })

      // 检测并标记无效变量
      this.highlightInvalidVariables(lineContent, line, variableNames)
    },

    // 高亮行中的无效变量
    highlightInvalidVariables(lineContent, line, validVariables) {
      // 正则表达式：匹配表名.字段名或表名_字段名格式的变量
      const variableRegex = /[a-zA-Z0-9\u4e00-\u9fa5]+[._][a-zA-Z0-9\u4e00-\u9fa5]+/g
      let match


      // 查找所有匹配的变量
      while ((match = variableRegex.exec(lineContent)) !== null) {
        const variable = match[0]
        const position = match.index

        // 检查变量是否已经被标记为有效变量
        const isAlreadyValid = validVariables.some(validVar => {
          const validVarLower = validVar.toLowerCase()
          const variableLower = variable.toLowerCase()
          return variableLower === validVarLower
        })

        // 如果变量无效且没有被标记为有效变量，并且不是纯数值的点或下划线组合，则标记为无效变量
        if (!isAlreadyValid) {
          // 检查是否是纯数值的点或下划线组合（如1.23或1_23）
          const isPureNumber = /^\d+[._]\d+$/.test(variable);
          if (!isPureNumber) {
            this.markTextRange(
              {line, ch: position},
              {line, ch: position + variable.length},
              'cm-invalid-field'
            );
          }
        }
      }
    },

    // 高亮行中的函数
    highlightFunctionsInLine(lineContent, line) {
      if (this.allFunctionNames.length === 0) return

      // 转换为小写用于不区分大小写的匹配
      const lowerLineContent = lineContent.toLowerCase()

      // 遍历所有函数名，长函数名优先匹配
      this.allFunctionNames.forEach(functionName => {
        const lowerFunctionName = functionName.toLowerCase()
        let position = 0

        // 使用不区分大小写的方式查找函数名
        while ((position = lowerLineContent.indexOf(lowerFunctionName, position)) !== -1) {
          // 确保原始内容中对应位置的字符是相同的（不区分大小写）
          if (lineContent.substring(position, position + functionName.length).toLowerCase() === lowerFunctionName) {
            // 检查函数名前面是否是边界字符
            const prevChar = position > 0 ? lineContent.charAt(position - 1) : ''
            // 检查函数名后面是否是边界字符或左括号
            const nextChar = lineContent.charAt(position + functionName.length)

            // 检查是否是完整的函数名
            const isStartOfWord = position === 0 || !/[a-zA-Z0-9_]/.test(prevChar)
            const isEndOfWord = !nextChar || !/[a-zA-Z0-9_]/.test(nextChar) || nextChar === '('

            if (isStartOfWord && isEndOfWord) {
              this.markTextRange(
                {line, ch: position},
                {line, ch: position + functionName.length},
                'cm-func'
              )
            }
          }
          position += functionName.length
        }
      })
    },

    // 在编辑器中标记文本
    markTextRange(from, to, className) {
      if (!this.codeMirrorEditor) return
      this.codeMirrorEditor.markText(from, to, {className})
    },

    // 聚焦公式编辑器
    focusEditor() {
      if (!this.codeMirrorEditor) return

      const cursorPosition = this.formulaStr ? this.formulaStr.length : 0
      this.codeMirrorEditor.setCursor({line: 0, ch: cursorPosition})
      this.codeMirrorEditor.focus()
    }
  }
}
</script>

<style lang="scss" scoped>
.formula-editor {
  .section-label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #333;
  }

  /deep/ .CodeMirror {
    height: 100px !important;
    border: 1px solid #dcdfe6;
    border-radius: 4px;

    .cm-func {
      font-weight: bold !important;
      color: #ae4597 !important;
      line-height: 14px;
      margin: 0 1px;
      padding: 0 2px;
    }
  }
}
</style>